import { Alert, App, Layout, Tabs, Typography, theme } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import MainMap, {
  type MapClickEvent,
  type RouteBbox,
} from '@/components/map/MainMap';
import FreightRestrictionPopup from '@/components/map/FreightRestrictionPopup';
import IndustrialRefBookCard from '@/components/map/IndustrialRefBookCard';
import MapLegend from '@/components/map/MapLegend';
import RecomputeStatusBadge from '@/components/RecomputeStatusBadge';
import Toolbar from '@/components/map/Toolbar';
import type { IndustrialRefBookRecord } from '@/types/industrialRefBook';
import { useAppConfig } from '@/hooks/useAppConfig';
import { useBarrierGeometry } from '@/hooks/useBarrierGeometry';
import { useDetourWaypoints } from '@/hooks/useDetourWaypoints';
import { buildBarrierSelectedWaypointsCollection } from '@/components/map/barrierSelectedWaypointsLayer';
import { useResizablePanelWidth } from '@/hooks/useResizablePanelWidth';
import { useSnapPoint } from '@/hooks/useSnapPoint';
import {
  useVisibleRoutesGeometry,
  type VariantCollections,
} from '@/hooks/useVisibleRoutesGeometry';
import { reverseGeocode } from '@/api/endpoints/geocode';
import { fetchIndustrialRefBookItem } from '@/api/endpoints/industrialRefBook';
import { useBarrierWorkspace } from '@/stores/BarrierWorkspaceContext';
import { useIndustrialFocus } from '@/stores/IndustrialFocusContext';
import { useMapLayers, type MapLayerKey } from '@/stores/MapLayersContext';
import { useMapTool } from '@/stores/MapToolContext';
import { useRouteWorkspace } from '@/stores/RouteWorkspaceContext';
import { useTileRevision } from '@/stores/TileRevisionContext';
import type { RouteVariantKey } from '@/types/routes';
import {
  PANEL_MODE_PATH,
  panelModeFromPath,
  readLastPanelMode,
  writeLastPanelMode,
  type PanelMode,
} from '@/utils/panelMode';

import { MAP_TAB, ROUTES, WORKSPACE_ROUTES } from '@/routes/paths';

const { Header, Sider, Content } = Layout;

// MapLibre resolves relative tile URLs against the *style* URL
// (https://tiles.openfreemap.org/...), NOT the document origin — so
// "/api/v1/tiles/..." would silently fail against openfreemap.org in
// production where VITE_API_BASE_URL is empty (same-origin via Ingress).
// Fall back to window.location.origin so tile URLs are always absolute.
const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || window.location.origin;

const TOOLBAR_WIDTH = 72;
const HEADER_HEIGHT = 64;
const DRAG_HANDLE_WIDTH_PX = 5;
const EMPTY_BARRIER_IDS: readonly number[] = [];

const LAYER_KEY_TO_VARIANT: Partial<Record<MapLayerKey, RouteVariantKey>> = {
  routes_gis: 'gis',
  routes_iteko: 'iteko',
  routes_barrier_aware: 'barrier_aware',
};

interface TabDefinition {
  readonly key: string;
  readonly labelKey: string;
}

// Top-menu items. The "map" virtual tab owns both `/routes` and
// `/barriers` URLs — users switch between those libraries via the
// in-panel segmented control, not via the top menu.
const TABS: readonly TabDefinition[] = [
  { key: MAP_TAB, labelKey: 'nav.map' },
  { key: ROUTES.DASHBOARD, labelKey: 'nav.dashboard' },
  { key: ROUTES.STATS, labelKey: 'nav.stats' },
  { key: ROUTES.INDUSTRIAL_REF_BOOK, labelKey: 'nav.industrialRefBook' },
];

export default function AppLayout() {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const { notification } = App.useApp();
  const location = useLocation();
  const navigate = useNavigate();
  const config = useAppConfig();
  const { snap } = useSnapPoint();
  const { mode, setMode } = useMapTool();
  const {
    draft: barrierDraft,
    selectedId: selectedBarrierId,
    focusedBarrierBbox,
    focusedWaypoint,
    flyToWaypoint,
    startDraft: startBarrierDraft,
    setDraftHintEndpoint,
    addDraftWaypoint,
    selectBarrier,
  } = useBarrierWorkspace();
  const {
    draft: routeDraft,
    focusedRouteId,
    visibleRouteIds,
    getVariantToggles,
    setOrigin: setRouteOrigin,
    setDestination: setRouteDestination,
    startDraft: startRouteDraft,
    focusRoute,
  } = useRouteWorkspace();
  const [pickedIndustrialId, setPickedIndustrialId] = useState<number | null>(null);
  const [pickedFreightWayId, setPickedFreightWayId] = useState<number | null>(null);
  const { pending: industrialFocusPending, clear: clearIndustrialFocus } =
    useIndustrialFocus();
  const { layers: mapLayers } = useMapLayers();
  const { barriersRevision, routesRevision } = useTileRevision();
  const panelWidth = useResizablePanelWidth();

  const activeVariants = useMemo<ReadonlySet<RouteVariantKey>>(() => {
    const out = new Set<RouteVariantKey>();
    for (const layer of mapLayers) {
      const variant = LAYER_KEY_TO_VARIANT[layer.key];
      if (!variant) continue;
      if (layer.visible && !layer.disabled) out.add(variant);
    }
    return out;
  }, [mapLayers]);

  const isVariantEnabled = useCallback(
    (routeId: string, variant: RouteVariantKey): boolean =>
      getVariantToggles(routeId)[variant],
    [getVariantToggles],
  );

  const { collections: routeCollections } = useVisibleRoutesGeometry({
    routeIds: visibleRouteIds,
    activeVariants,
    isVariantEnabled,
    revision: routesRevision,
  });

  const focusedRouteBbox = useMemo<RouteBbox | null>(
    () => _computeFocusBbox(routeCollections, focusedRouteId),
    [routeCollections, focusedRouteId],
  );

  const residualBarrierIds = useMemo<readonly number[]>(
    () => _readResidualBarriers(routeCollections, focusedRouteId),
    [routeCollections, focusedRouteId],
  );

  const detourCollection = useDetourWaypoints(routeCollections);

  // Show the selected barrier's configured `auto_waypoints` on the map
  // (outline diamonds) so users see what they configured even before any
  // route exercises them. Reuses the existing geometry hook — same
  // request as `BarrierPropertiesPanel`, served from the server-side
  // Redis cache after the first hit.
  const selectedBarrierGeometry = useBarrierGeometry(selectedBarrierId);
  const barrierSelectedWaypoints = useMemo(
    () =>
      buildBarrierSelectedWaypointsCollection(
        selectedBarrierId,
        selectedBarrierGeometry.status === 'success'
          ? selectedBarrierGeometry.feature.properties.auto_waypoints
          : null,
      ),
    [selectedBarrierId, selectedBarrierGeometry],
  );

  // Row click in the Industrial reference-book table flies the map to the
  // object and opens its card. The table navigates here (to a workspace
  // route); the map mounts and its focusedWaypoint effect runs on `load`.
  useEffect(() => {
    if (industrialFocusPending === null) return;
    flyToWaypoint({
      lat: industrialFocusPending.lat,
      lon: industrialFocusPending.lon,
    });
    setPickedIndustrialId(industrialFocusPending.id);
    clearIndustrialFocus();
  }, [industrialFocusPending, flyToWaypoint, clearIndustrialFocus]);

  const isWorkspace = WORKSPACE_ROUTES.includes(location.pathname);
  const currentPanelMode = panelModeFromPath(location.pathname);

  const tabItems = useMemo(
    () => TABS.map(({ key, labelKey }) => ({ key, label: t(labelKey) })),
    [t],
  );

  const activeTab = useMemo(() => {
    if (currentPanelMode !== null) return MAP_TAB;
    const match = TABS.find((tab) => tab.key === location.pathname);
    return match?.key ?? MAP_TAB;
  }, [currentPanelMode, location.pathname]);

  const handleTabChange = useCallback(
    (key: string) => {
      if (key === MAP_TAB) {
        // Preserve current panel mode on re-click; fall back to the
        // persisted preference only when coming from Dashboard/Stats.
        const target = currentPanelMode ?? readLastPanelMode();
        navigate(PANEL_MODE_PATH[target]);
        return;
      }
      navigate(key);
    },
    [currentPanelMode, navigate],
  );

  const navigateToPanelMode = useCallback(
    (target: PanelMode) => {
      const path = PANEL_MODE_PATH[target];
      writeLastPanelMode(target);
      if (location.pathname !== path) navigate(path);
    },
    [location.pathname, navigate],
  );

  // A draft can start from outside this component (toolbar buttons,
  // industrial-card shortcuts). On the RISING EDGE (null → non-null)
  // only, route the URL to the matching workspace — so the user ends
  // up on the correct library when the draft finishes. Subsequent
  // location changes must NOT re-trigger the navigation (otherwise
  // users couldn't visit Dashboard/Stats while drafting). Refs hold
  // the latest navigate/pathname so this effect's dep list can stay
  // tight on just the draft value.
  const navigateRef = useRef(navigate);
  const pathnameRef = useRef(location.pathname);
  useEffect(() => {
    navigateRef.current = navigate;
    pathnameRef.current = location.pathname;
  });
  const prevRouteDraftRef = useRef(routeDraft);
  useEffect(() => {
    if (prevRouteDraftRef.current === null && routeDraft !== null) {
      writeLastPanelMode('routes');
      if (pathnameRef.current !== PANEL_MODE_PATH.routes) {
        navigateRef.current(PANEL_MODE_PATH.routes);
      }
    }
    prevRouteDraftRef.current = routeDraft;
  }, [routeDraft]);
  const prevBarrierDraftRef = useRef(barrierDraft);
  useEffect(() => {
    if (prevBarrierDraftRef.current === null && barrierDraft !== null) {
      writeLastPanelMode('barriers');
      if (pathnameRef.current !== PANEL_MODE_PATH.barriers) {
        navigateRef.current(PANEL_MODE_PATH.barriers);
      }
    }
    prevBarrierDraftRef.current = barrierDraft;
  }, [barrierDraft]);

  const warnNotOnRoad = useCallback(() => {
    notification.warning({
      message: t('snap.notOnRoad.title'),
      description: t('snap.notOnRoad.description'),
    });
  }, [notification, t]);

  const safeSnap = useCallback(
    async (lat: number, lon: number) => {
      try {
        return await snap(lat, lon);
      } catch (error) {
        notification.error({
          message: t('snap.errorTitle'),
          description: error instanceof Error ? error.message : t('errors.unexpected'),
        });
        return null;
      }
    },
    [notification, snap, t],
  );

  const safeReverseGeocode = useCallback(
    async (lat: number, lon: number) => {
      try {
        return await reverseGeocode({ lat, lon });
      } catch {
        return null;
      }
    },
    [],
  );

  const handleMapClick = async (event: MapClickEvent) => {
    const {
      point, barrierId, routeId, detourBarrierId,
      industrialRefBookId,
      freightRestrictionWayId,
    } = event;

    // Freight-restriction clicks open a read-only popup. Suppress them
    // when the user is busy with a tool that consumes map clicks
    // (creating a barrier, picking route endpoints, etc.) — otherwise a
    // popup hijacks the intended interaction. In those modes we fall
    // through to the regular handlers below; the click then snaps the
    // coordinate on the road as expected.
    if (freightRestrictionWayId !== null && (mode === 'pan' || mode === 'select')) {
      setPickedFreightWayId(freightRestrictionWayId);
      return;
    }

    if (industrialRefBookId !== null) {
      if (mode === 'pick_route_origin' || mode === 'pick_route_destination') {
        try {
          const record = await fetchIndustrialRefBookItem(industrialRefBookId);
          const endpoint = _industrialEndpoint(record);
          if (mode === 'pick_route_origin') setRouteOrigin(endpoint);
          else setRouteDestination(endpoint);
          setMode('create_route');
        } catch (error) {
          notification.error({
            message: t('industrialRefBook.error.fetchTitle'),
            description: error instanceof Error ? error.message : t('errors.unexpected'),
          });
        }
        return;
      }
      setPickedIndustrialId(industrialRefBookId);
      return;
    }

    if (detourBarrierId !== null) {
      notification.info({
        message: t('detours.clickInfo.title'),
        description: t('detours.clickInfo.description', { id: detourBarrierId }),
        duration: 3,
      });
      selectBarrier(detourBarrierId);
      navigateToPanelMode('barriers');
      return;
    }

    if (mode === 'select' || mode === 'pan') {
      if (routeId !== null) {
        selectBarrier(null);
        focusRoute(routeId);
        navigateToPanelMode('routes');
        if (mode === 'pan') setMode('select');
        return;
      }
      if (barrierId !== null) {
        focusRoute(null);
        selectBarrier(barrierId);
        navigateToPanelMode('barriers');
        if (mode === 'pan') setMode('select');
        return;
      }
      if (mode === 'select') {
        focusRoute(null);
        selectBarrier(null);
      }
      return;
    }
    if (mode === 'create_barrier') {
      if (barrierDraft !== null) return;
      if (config.status !== 'success') return;
      const snapped = await safeSnap(point.lat, point.lon);
      if (!snapped) { warnNotOnRoad(); return; }
      startBarrierDraft(
        { lat: snapped.lat, lon: snapped.lon },
        snapped.road_name,
        config.data.barrier_default_radius_m,
      );
      navigateToPanelMode('barriers');
      return;
    }
    if (mode === 'pick_direction_hint') {
      const snapped = await safeSnap(point.lat, point.lon);
      if (!snapped) { warnNotOnRoad(); return; }
      setDraftHintEndpoint({ lat: snapped.lat, lon: snapped.lon });
      setMode('create_barrier');
      return;
    }
    if (mode === 'add_waypoint') {
      const snapped = await safeSnap(point.lat, point.lon);
      if (!snapped) { warnNotOnRoad(); return; }
      addDraftWaypoint({ lat: snapped.lat, lon: snapped.lon });
      setMode('create_barrier');
      return;
    }
    if (mode === 'pick_route_origin' || mode === 'pick_route_destination') {
      const snapped = await safeSnap(point.lat, point.lon);
      if (!snapped) { warnNotOnRoad(); return; }
      const address = await safeReverseGeocode(snapped.lat, snapped.lon);
      const endpoint = {
        lat: snapped.lat,
        lon: snapped.lon,
        address: address?.label ?? snapped.road_name ?? null,
      };
      if (mode === 'pick_route_origin') setRouteOrigin(endpoint);
      else setRouteDestination(endpoint);
      setMode('create_route');
      return;
    }
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: token.margin,
          paddingInline: token.padding,
          background: token.colorBgContainer,
          borderBottom: `1px solid ${token.colorBorder}`,
          height: HEADER_HEIGHT,
        }}
      >
        <Typography.Title level={4} style={{ margin: 0, flexShrink: 0 }}>
          {t('app.title')}
        </Typography.Title>
        <Tabs
          activeKey={activeTab}
          items={tabItems}
          onChange={handleTabChange}
          style={{ flex: 1, minWidth: 0 }}
          tabBarStyle={{ margin: 0 }}
        />
        <RecomputeStatusBadge />
      </Header>

      {config.status === 'error' && (
        <Alert
          type="error"
          showIcon
          banner
          message={t('app.configError', { message: config.message })}
        />
      )}

      <Layout>
        {/* Toolbar + map stay mounted across tab switches (hidden via
            display:none off the workspace) so the map preserves its camera
            and state instead of resetting on every tab change. */}
        <Sider
          width={TOOLBAR_WIDTH}
          style={{
            background: token.colorBgContainer,
            borderRight: `1px solid ${token.colorBorder}`,
            display: isWorkspace ? 'block' : 'none',
          }}
        >
          <Toolbar />
        </Sider>
        <Content
          style={{
            position: 'relative',
            minHeight: 'calc(100vh - 64px)',
            display: isWorkspace ? 'block' : 'none',
          }}
        >
          <MainMap
            apiBaseUrl={API_BASE_URL}
            barriersRevision={barriersRevision}
            selectedBarrierId={selectedBarrierId}
            focusedRouteId={focusedRouteId}
            focusedRouteBbox={focusedRouteBbox}
            focusedBarrierBbox={focusedBarrierBbox}
            focusedWaypoint={focusedWaypoint}
            residualBarrierIds={residualBarrierIds}
            routeCollections={routeCollections}
            detourCollection={detourCollection}
            barrierSelectedWaypoints={barrierSelectedWaypoints}
            barrierDraft={barrierDraft}
            routeDraft={routeDraft}
            mapLayers={mapLayers}
            mode={mode}
            visible={isWorkspace}
            onMapClick={handleMapClick}
          />
          <MapLegend />
          {pickedIndustrialId !== null && (
            <IndustrialRefBookCard
              itemId={pickedIndustrialId}
              onUseAsOrigin={(record) => {
                if (!routeDraft) startRouteDraft();
                setRouteOrigin(_industrialEndpoint(record));
                setMode('create_route');
                setPickedIndustrialId(null);
                navigateToPanelMode('routes');
              }}
              onUseAsDestination={(record) => {
                if (!routeDraft) startRouteDraft();
                setRouteDestination(_industrialEndpoint(record));
                setMode('create_route');
                setPickedIndustrialId(null);
                navigateToPanelMode('routes');
              }}
              onClose={() => setPickedIndustrialId(null)}
            />
          )}
          {pickedFreightWayId !== null && (
            <FreightRestrictionPopup
              wayId={pickedFreightWayId}
              onClose={() => setPickedFreightWayId(null)}
            />
          )}
        </Content>
        {isWorkspace ? (
          <Sider
            width={panelWidth.width}
            style={{
              background: token.colorBgContainer,
              borderLeft: `1px solid ${token.colorBorder}`,
              padding: token.padding,
              overflowY: 'auto',
              position: 'relative',
            }}
          >
            <div
              onMouseDown={panelWidth.startDrag}
              role="separator"
              aria-orientation="vertical"
              aria-label={t('workspace.resizeHandle')}
              style={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: -Math.floor(DRAG_HANDLE_WIDTH_PX / 2),
                width: DRAG_HANDLE_WIDTH_PX,
                cursor: 'col-resize',
                zIndex: 10,
                background: panelWidth.dragging ? token.colorPrimary : 'transparent',
                transition: 'background 160ms ease',
              }}
            />
            <Outlet />
          </Sider>
        ) : (
          <Content style={{ padding: token.paddingLG, overflowY: 'auto' }}>
            <Outlet />
          </Content>
        )}
      </Layout>
    </Layout>
  );
}

function _computeFocusBbox(
  collections: VariantCollections,
  focusedRouteId: string | null,
): RouteBbox | null {
  if (focusedRouteId === null) return null;
  // Prefer barrier_aware for framing: it's the canonical user-facing
  // path and includes any detour segments.
  for (const variant of ['barrier_aware', 'gis', 'iteko'] as const) {
    const feature = collections[variant].features.find(
      (f) => f.id === focusedRouteId,
    );
    if (feature) {
      const coords = feature.geometry.coordinates.map(
        ([lon, lat]) => [lon, lat] as readonly [number, number],
      );
      return _computeBbox(coords);
    }
  }
  return null;
}

function _readResidualBarriers(
  collections: VariantCollections,
  focusedRouteId: string | null,
): readonly number[] {
  if (focusedRouteId === null) return EMPTY_BARRIER_IDS;
  const feature = collections.barrier_aware.features.find(
    (f) => f.id === focusedRouteId,
  );
  if (!feature || !feature.properties) return EMPTY_BARRIER_IDS;
  const raw = (feature.properties as { residual_barriers?: unknown })
    .residual_barriers;
  if (!Array.isArray(raw)) return EMPTY_BARRIER_IDS;
  return raw.filter((n): n is number => typeof n === 'number');
}

function _industrialEndpoint(record: IndustrialRefBookRecord) {
  return {
    lat: record.latitude,
    lon: record.longitude,
    address: record.short_title ?? record.title,
  };
}

function _computeBbox(
  coordinates: ReadonlyArray<readonly [number, number]>,
): RouteBbox | null {
  if (coordinates.length === 0) return null;
  let west = Number.POSITIVE_INFINITY;
  let south = Number.POSITIVE_INFINITY;
  let east = Number.NEGATIVE_INFINITY;
  let north = Number.NEGATIVE_INFINITY;
  for (const [lon, lat] of coordinates) {
    if (lon < west) west = lon;
    if (lon > east) east = lon;
    if (lat < south) south = lat;
    if (lat > north) north = lat;
  }
  return [west, south, east, north];
}
